import { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, TIER_LABEL } from "../../lib/api";
import { ChatThread } from "../ChatThread";

export function Inbox({ onRead }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);

  const loadThreads = useCallback(() => {
    api.get("/admin/threads").then((r) => setThreads(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 8000);
    return () => clearInterval(t);
  }, [loadThreads]);

  useEffect(() => {
    if (!active) return undefined;
    const load = () => api.get(`/admin/threads/${active.user_id}`).then((r) => setMessages(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    load();
    onRead?.();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [active, onRead]);

  const send = async (text) => {
    try {
      const { data } = await api.post(`/admin/threads/${active.user_id}`, { text });
      setMessages((m) => [...m, data]);
      loadThreads();
      return true;
    } catch (e) {
      toast.error(formatError(e));
      return false;
    }
  };

  return (
    <div data-testid="admin-inbox" className="border border-[#0E1B3D]/[0.08] bg-[#FFFFFF] grid md:grid-cols-[300px_1fr] h-[620px]">
      <div className={`border-r border-[#0E1B3D]/[0.08] overflow-y-auto ${active ? "hidden md:block" : ""}`} data-lenis-prevent>
        {threads.length === 0 && <p className="p-6 text-sm text-[#4A5878]">No conversations yet. Any subscriber can message you.</p>}
        {threads.map((t) => (
          <button key={t.user_id} data-testid="admin-thread-item" onClick={() => setActive(t)}
            className={`w-full text-left px-5 py-4 border-b border-[#0E1B3D]/[0.06] transition-colors ${active?.user_id === t.user_id ? "bg-[#E6F3FC]" : "hover:bg-[#F1F8FD]"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-[#0E1B3D] truncate">{t.name}</span>
              {t.unread > 0 && <span className="text-[0.65rem] bg-[#D7263D] text-[#FFF8EE] px-1.5 py-0.5 font-bold">{t.unread}</span>}
            </div>
            <p className="text-xs text-[#D7263D]/90 mt-0.5">{TIER_LABEL[t.tier] || "No tier"}</p>
            <p className="text-xs text-[#4A5878] truncate mt-1">{t.last}</p>
            <p className="text-[0.65rem] text-[#6B7894] mt-1">{t.last_at ? formatDistanceToNow(new Date(t.last_at), { addSuffix: true }) : ""}</p>
          </button>
        ))}
      </div>
      <div className={`flex flex-col min-h-0 ${active ? "" : "hidden md:flex"}`}>
        {active ? (
          <>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#0E1B3D]/[0.08]">
              <button data-testid="admin-thread-back-btn" onClick={() => setActive(null)} className="md:hidden text-[#26355A]" aria-label="Back"><ArrowLeft className="w-4 h-4" /></button>
              <p className="font-display text-xl text-[#0E1B3D]">{active.name}</p>
            </div>
            <ChatThread messages={messages} mine="creator" onSend={send} placeholder="Reply…" />
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-sm text-[#4A5878]">Select a conversation</div>
        )}
      </div>
    </div>
  );
}
